import React, { useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, ChevronDown, Ticket, MapPin, Clock, Users, Image, BookOpen, Map, CalendarDays, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";

// Language configuration
const languages = [
  { code: "fr", name: "Français", country: "fr" },
  { code: "en", name: "English", country: "gb" },
  { code: "ar", name: "العربية", country: "ma" },
  { code: "nl", name: "Nederlands", country: "nl" },
  { code: "es", name: "Español", country: "es" },
];

// Flag component
const Flag = ({ country, size = 20 }) => (
  <img 
    src={`https://hatscripts.github.io/circle-flags/flags/${country}.svg`}
    width={size}
    height={size}
    alt={country}
    className="rounded-full object-cover flex-shrink-0"
    style={{ minWidth: size, minHeight: size }}
    loading="eager"
  />
);

// Hook for localized links
const useLocalizedLink = () => {
  const { lang } = useParams();
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  return (path) => {
    if (path.startsWith("/admin") || path.startsWith("/collaborator")) {
      return path;
    }
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };
};

// Departure cities for 4x4
const departureCities = [
  { id: "marrakech", name: "Marrakech" },
  { id: "ouarzazate", name: "Ouarzazate" },
  { id: "agadir", name: "Agadir" },
  { id: "zagora", name: "Zagora" },
];

// Trek durations
const trekDurations = [
  { id: "court", label: { fr: "Courts (2-5j)", en: "Short (2-5d)" } },
  { id: "moyen", label: { fr: "Moyens (6-9j)", en: "Medium (6-9d)" } },
  { id: "long", label: { fr: "Longs (10+j)", en: "Long (10+d)" } },
];

// About submenu items
const aboutItems = [
  { path: "/team", label: { fr: "Notre Équipe", en: "Our Team" }, icon: Users },
  { path: "/experiences", label: { fr: "Retour d'expériences", en: "Traveler Experiences" }, icon: MessageCircle },
  { path: "/gallery", label: { fr: "Galerie Photos", en: "Photo Gallery" }, icon: Image },
  { path: "/blog", label: { fr: "Blog", en: "Blog" }, icon: BookOpen },
  { path: "/map", label: { fr: "Carte Interactive", en: "Interactive Map" }, icon: Map },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [circuits4x4Open, setCircuits4x4Open] = useState(false);
  const [treksOpen, setTreksOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  
  const localizedLink = useLocalizedLink();
  const currentLang = lang || i18n.language || "fr";

  const isActive = (path) => {
    const localizedPath = localizedLink(path);
    if (path === "/") return location.pathname === localizedPath || location.pathname === `/${currentLang}` || location.pathname === `/${currentLang}/`;
    return location.pathname.startsWith(localizedPath);
  };

  const isAboutActive = () => aboutItems.some(item => location.pathname.includes(item.path.slice(1)));

  const changeLanguage = (code) => {
    // Get current path without language prefix
    const pathParts = location.pathname.split('/');
    pathParts[1] = code; // Replace language code
    const newPath = pathParts.join('/') || `/${code}/`;
    
    i18n.changeLanguage(code);
    navigate(newPath);
  };

  const currentLangObj = languages.find((l) => l.code === currentLang) || languages[0];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <nav className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to={localizedLink("/")} className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <span className="text-white font-serif text-lg">X</span>
            </div>
            <span className="font-serif text-lg md:text-xl font-semibold text-secondary hidden sm:block">
              XploreSahara
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {/* Home */}
            <Link
              to={localizedLink("/")}
              data-testid="nav-home"
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive("/")
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {t("nav.home")}
            </Link>

            {/* 4x4 Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-colors bg-transparent h-auto ${
                      isActive("/circuits/4x4") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                    data-testid="nav-circuits-4x4-dropdown"
                  >
                    4x4
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-52 p-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> Départ de
                      </p>
                      {departureCities.map((city) => (
                        <NavigationMenuLink key={city.id} asChild>
                          <Link
                            to={localizedLink(`/circuits/4x4?city=${city.id}`)}
                            className="block px-3 py-2 rounded-md text-sm hover:bg-primary/10 hover:text-primary transition-colors"
                          >
                            {city.name}
                          </Link>
                        </NavigationMenuLink>
                      ))}
                      <div className="border-t mt-1 pt-1">
                        <NavigationMenuLink asChild>
                          <Link to={localizedLink("/circuits/4x4")} className="block px-3 py-2 rounded-md text-sm font-medium text-primary hover:bg-primary/10">
                            Tous les circuits →
                          </Link>
                        </NavigationMenuLink>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Treks Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-colors bg-transparent h-auto ${
                      isActive("/circuits/trekking") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                    data-testid="nav-trekking-dropdown"
                  >
                    Treks
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-48 p-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Durée
                      </p>
                      {trekDurations.map((duration) => (
                        <NavigationMenuLink key={duration.id} asChild>
                          <Link
                            to={localizedLink(`/circuits/trekking?duration=${duration.id}`)}
                            className="block px-3 py-2 rounded-md text-sm hover:bg-primary/10 hover:text-primary transition-colors"
                          >
                            {duration.label[currentLang] || duration.label.fr}
                          </Link>
                        </NavigationMenuLink>
                      ))}
                      <div className="border-t mt-1 pt-1">
                        <NavigationMenuLink asChild>
                          <Link to={localizedLink("/circuits/trekking")} className="block px-3 py-2 rounded-md text-sm font-medium text-primary hover:bg-primary/10">
                            Tous les treks →
                          </Link>
                        </NavigationMenuLink>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Stages */}
            <Link
              to={localizedLink("/stages")}
              data-testid="nav-stages"
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive("/stages") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              Stages
            </Link>

            {/* Prochains Départs */}
            <Link
              to={localizedLink("/departures")}
              data-testid="nav-departures"
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                isActive("/departures") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {currentLang === "fr" ? "Départs" : "Departures"}
            </Link>

            {/* About Us Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-colors bg-transparent h-auto ${
                      isAboutActive() ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                    data-testid="nav-about-dropdown"
                  >
                    {currentLang === "fr" ? "À propos" : "About"}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-52 p-2">
                      {aboutItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavigationMenuLink key={item.path} asChild>
                            <Link
                              to={localizedLink(item.path)}
                              className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                isActive(item.path) 
                                  ? "bg-primary/10 text-primary" 
                                  : "hover:bg-primary/10 hover:text-primary"
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                              {item.label[currentLang] || item.label.fr}
                            </Link>
                          </NavigationMenuLink>
                        );
                      })}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Contact */}
            <Link
              to={localizedLink("/contact")}
              data-testid="nav-contact"
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive("/contact") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            {/* My Reservations */}
            <Link to={localizedLink("/my-reservations")} className="hidden md:block">
              <Button variant="ghost" size="sm" className="gap-1.5 h-9 px-3" data-testid="my-reservations-link">
                <Ticket className="h-4 w-4" />
                <span className="hidden xl:inline text-sm">Réservations</span>
              </Button>
            </Link>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 h-9 px-2" data-testid="language-selector">
                  <Flag country={currentLangObj.country} size={20} />
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {languages.map((lang) => (
                  <DropdownMenuItem key={lang.code} onClick={() => changeLanguage(lang.code)} className="gap-2 cursor-pointer">
                    <Flag country={lang.country} size={18} />
                    <span>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button */}
            <Link to={localizedLink("/circuits")} className="hidden md:block">
              <Button className="rounded-full bg-primary hover:bg-primary/90 px-5 h-9 text-sm" data-testid="nav-book-btn">
                {t("nav.book")}
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 overflow-y-auto">
                <div className="flex flex-col gap-2 mt-8">
                  {/* Home */}
                  <Link to={localizedLink("/")} onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 rounded-xl text-lg font-medium ${location.pathname === "/" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                    {t("nav.home")}
                  </Link>

                  {/* 4x4 Accordion */}
                  <div className="border rounded-xl overflow-hidden">
                    <button onClick={() => setCircuits4x4Open(!circuits4x4Open)}
                      className={`w-full px-4 py-3 text-lg font-medium flex items-center justify-between ${isActive("/circuits/4x4") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                      Circuits 4x4
                      <ChevronDown className={`h-4 w-4 transition-transform ${circuits4x4Open ? "rotate-180" : ""}`} />
                    </button>
                    {circuits4x4Open && (
                      <div className="bg-muted/50 px-2 pb-2">
                        {departureCities.map((city) => (
                          <Link key={city.id} to={localizedLink(`/circuits/4x4?city=${city.id}`)} onClick={() => setMobileOpen(false)}
                            className="block px-4 py-2 text-sm hover:text-primary">
                            Départ de {city.name}
                          </Link>
                        ))}
                        <Link to={localizedLink("/circuits/4x4")} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-medium text-primary">
                          Tous les circuits
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Treks Accordion */}
                  <div className="border rounded-xl overflow-hidden">
                    <button onClick={() => setTreksOpen(!treksOpen)}
                      className={`w-full px-4 py-3 text-lg font-medium flex items-center justify-between ${isActive("/circuits/trekking") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                      Treks
                      <ChevronDown className={`h-4 w-4 transition-transform ${treksOpen ? "rotate-180" : ""}`} />
                    </button>
                    {treksOpen && (
                      <div className="bg-muted/50 px-2 pb-2">
                        {trekDurations.map((d) => (
                          <Link key={d.id} to={localizedLink(`/circuits/trekking?duration=${d.id}`)} onClick={() => setMobileOpen(false)}
                            className="block px-4 py-2 text-sm hover:text-primary">
                            {d.label[currentLang] || d.label.fr}
                          </Link>
                        ))}
                        <Link to={localizedLink("/circuits/trekking")} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-medium text-primary">
                          Tous les treks
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Stages */}
                  <Link to={localizedLink("/stages")} onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 rounded-xl text-lg font-medium ${isActive("/stages") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                    Stages & Retraites
                  </Link>

                  {/* Prochains Départs */}
                  <Link to={localizedLink("/departures")} onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 rounded-xl text-lg font-medium flex items-center gap-2 ${isActive("/departures") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                    <CalendarDays className="h-5 w-5" />
                    {currentLang === "fr" ? "Prochains Départs" : "Upcoming Departures"}
                  </Link>

                  {/* About Accordion */}
                  <div className="border rounded-xl overflow-hidden">
                    <button onClick={() => setAboutOpen(!aboutOpen)}
                      className={`w-full px-4 py-3 text-lg font-medium flex items-center justify-between ${isAboutActive() ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                      {currentLang === "fr" ? "À propos" : "About"}
                      <ChevronDown className={`h-4 w-4 transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
                    </button>
                    {aboutOpen && (
                      <div className="bg-muted/50 px-2 pb-2">
                        {aboutItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link key={item.path} to={localizedLink(item.path)} onClick={() => setMobileOpen(false)}
                              className="flex items-center gap-2 px-4 py-2 text-sm hover:text-primary">
                              <Icon className="h-4 w-4" />
                              {item.label[currentLang] || item.label.fr}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Contact */}
                  <Link to={localizedLink("/contact")} onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 rounded-xl text-lg font-medium ${isActive("/contact") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                    Contact
                  </Link>

                  {/* Language selector mobile */}
                  <div className="px-4 py-3 border-t mt-2">
                    <p className="text-sm text-muted-foreground mb-2">Langue</p>
                    <div className="flex flex-wrap gap-2">
                      {languages.map((lang) => (
                        <button key={lang.code} onClick={() => changeLanguage(lang.code)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${currentLang === lang.code ? "bg-primary/10 text-primary border border-primary/30" : "bg-muted hover:bg-muted/80"}`}>
                          <Flag country={lang.country} size={16} />
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="mt-4 space-y-2">
                    <Link to={localizedLink("/circuits")} onClick={() => setMobileOpen(false)}>
                      <Button className="w-full rounded-full bg-primary hover:bg-primary/90">{t("nav.book")}</Button>
                    </Link>
                    <Link to={localizedLink("/my-reservations")} onClick={() => setMobileOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full gap-2">
                        <Ticket className="h-4 w-4" /> Mes réservations
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
